
import React, { Component } from 'react';

import { Icon, IconMenuThumb } from '../../../base/icons';
import {
    PARTICIPANT_ROLE,
    getLocalParticipant,
    getParticipantById
} from '../../../base/participants';
import { Popover } from '../../../base/popover';
import { connect } from '../../../base/redux';
import { requestRemoteControl, stopController } from '../../../remote-control';
import { LAYOUTS, getCurrentLayout } from '../../../video-layout';
import { iAmVisitor } from '../../../visitors/functions';

import { REMOTE_CONTROL_MENU_STATES } from './RemoteControlButton';

import {
    PrivateMessageMenuButton,
    RemoteControlButton,
    VideoMenu
} from './';

/**
 * The type of the React {@code Component} props of
 * {@link RemoteVideoMenuTriggerButton}.
 */
type Props = {

  /**
   * Whether or not to display the kick button.
   */
  _disableKick: boolean,

  /**
   * Whether or not to display the remote mute buttons.
   */
  _disableRemoteMute: Boolean,

  /**
   * Whether or not the participant is a conference moderator.
   */
  _isModerator: boolean,

  /**
   * The position relative to the trigger the remote menu should display
   * from. Valid values are those supported by AtlasKit
   * {@code InlineDialog}.
   */
  _menuPosition: string,

  /**
   * Whether to display the Popover as a drawer.
   */
  _overflowDrawer: boolean,

  /**
   * The current state of the participant's remote control session.
   */
  _remoteControlState: number,

  /**
   * The redux dispatch function.
   */
  dispatch: Function,

  /**
   * A value between 0 and 1 indicating the volume of the participant's
   * audio element.
   */
  initialVolumeValue: ?number,

  /**
   * Callback to invoke when changing the level of the participant's
   * audio element.
   */
  onVolumeChange: Function,

  /**
   * The ID for the participant on which the remote video menu will act.
   */
  participantID: string,


  /**   * Whether the current user is a visitor.
   */
  _iAmVisitor: boolean
};

/**
 * React {@code Component} for displaying an icon associated with opening the
 * the {@code VideoMenu}.
 *
 * @augments {Component}
 */
class RemoteVideoMenuTriggerButton extends Component<Props> {
    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const content = this._renderRemoteVideoMenu();

        if (!content) {
            return null;
        }

        return (
            <Popover
                content = { <div className = 'remote-video-menu-popup'>{content}</div> }
                overflowDrawer = { this.props._overflowDrawer }
                position = { this.props._menuPosition }>
                <span className = 'popover-trigger remote-video-menu-trigger'>
                    <Icon
                        size = '20'
                        src = { IconMenuThumb }
                        title = 'Remote user controls' />
                </span>
            </Popover>
        );
    }

    /**
   * Creates a new {@code VideoMenu} with buttons for interacting with
   * the remote participant.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderRemoteVideoMenu() {
        const {
            dispatch,
            _remoteControlState,
            participantID,
            _iAmVisitor

            // _visitorsSupported,
            // disableDemote
        } = this.props;

        const buttons = [];

        if (_remoteControlState) {
            let onRemoteControlToggle = null;

            if (_remoteControlState === REMOTE_CONTROL_MENU_STATES.STARTED) {
                onRemoteControlToggle = () => dispatch(stopController(true));
            } else if (
                _remoteControlState === REMOTE_CONTROL_MENU_STATES.NOT_STARTED
            ) {
                onRemoteControlToggle = () =>
                    dispatch(requestRemoteControl(participantID));
            }

            buttons.push(
        <RemoteControlButton
            key = 'remote-control'
            onClick = { onRemoteControlToggle }
            participantID = { participantID }
            remoteControlState = { _remoteControlState } />
            );
        }

        if (!_iAmVisitor) {
            buttons.push(
          <PrivateMessageMenuButton
              dispatch = { dispatch }
              key = 'privateMessage'
              participantID = { participantID } />
            );
        }


        // if (!disableDemote && _visitorsSupported && _isModerator) {
        //     buttons.push(<DemoteToVisitorButton { ...getButtonProps(BUTTONS.DEMOTE) } />);
        // }

        // buttons.push();

        //     if (
        //         onVolumeChange
        //   && typeof initialVolumeValue === 'number'
        //   && !isNaN(initialVolumeValue)
        //     ) {
        //         buttons.push(
        //     <VolumeSlider
        //         initialValue = { initialVolumeValue }
        //         key = 'volume-slider'
        //         onChange = { onVolumeChange } />
        //         );
        //     }

        if (buttons.length > 0) {
            return <VideoMenu id = { participantID }>{buttons}</VideoMenu>;
        }

        return null;
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code RemoteVideoMenuTriggerButton}'s props.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state, ownProps) {
    const { participantID } = ownProps;
    const localParticipant = getLocalParticipant(state);
    const { remoteVideoMenu = {}, disableRemoteMute }
    = state['features/base/config'];
    const { disableKick } = remoteVideoMenu;
    let _remoteControlState = null;
    const participant = getParticipantById(state, participantID);
    const _isRemoteControlSessionActive
    = participant?.remoteControlSessionStatus ?? false;
    const _supportsRemoteControl = participant?.supportsRemoteControl ?? false;
    const { active, controller } = state['features/remote-control'];
    const { requestedParticipant, controlled } = controller;
    const activeParticipant = requestedParticipant || controlled;
    const { overflowDrawer } = state['features/toolbox'];

    if (
        _supportsRemoteControl
    && ((!active && !_isRemoteControlSessionActive)
      || activeParticipant === participantID)
    ) {
        if (requestedParticipant === participantID) {
            _remoteControlState = REMOTE_CONTROL_MENU_STATES.REQUESTING;
        } else if (controlled) {
            _remoteControlState = REMOTE_CONTROL_MENU_STATES.STARTED;
        } else {
            _remoteControlState = REMOTE_CONTROL_MENU_STATES.NOT_STARTED;
        }
    }

    const visitorsSupported = state['features/visitors'].supported;


    const currentLayout = getCurrentLayout(state);
    let _menuPosition;

    switch (currentLayout) {
    case LAYOUTS.TILE_VIEW:
        _menuPosition = 'left-end';
        break;
    case LAYOUTS.VERTICAL_FILMSTRIP_VIEW:
        _menuPosition = 'bottom-end';
        break;
    default:
        _menuPosition = 'auto';
    }

    return {
        _isModerator: Boolean(
      localParticipant?.role === PARTICIPANT_ROLE.MODERATOR
        ),
        _visitorsSupported: visitorsSupported,
        _iAmVisitor: iAmVisitor(state),
        _disableKick: Boolean(disableKick),
        _disableRemoteMute: Boolean(disableRemoteMute),
        _remoteControlState,
        _menuPosition,
        _overflowDrawer: overflowDrawer
    };
}

export default connect(_mapStateToProps)(RemoteVideoMenuTriggerButton);
